import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowRight, CalendarDays, MapPin, GraduationCap, HeartHandshake, Users, Sparkles } from "lucide-react";

const heroImage = "https://images.pexels.com/photos/26867471/pexels-photo-26867471.jpeg";

const pillars = [
  {
    icon: GraduationCap,
    title: "Clinical Education",
    body: "Case-based CME, journal clubs, and buprenorphine workshops built by Arizona clinicians, for Arizona clinicians.",
  },
  {
    icon: HeartHandshake,
    title: "Advocacy in Action",
    body: "Advancing policy that expands access to medications for opioid use disorder, harm reduction, and integrated care.",
  },
  {
    icon: Users,
    title: "Community of Peers",
    body: "A statewide network from Yuma to Flagstaff — resident to seasoned addiction specialist — walking this work together.",
  },
];

export default function Home() {
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);
  const [board, setBoard] = useState([]);

  useEffect(() => {
    api.get("/events", { params: { scope: "upcoming" } }).then((r) => setEvents(r.data.slice(0, 3))).catch(() => {});
    api.get("/news").then((r) => setNews(r.data.slice(0, 2))).catch(() => {});
    api.get("/board").then((r) => setBoard(r.data.slice(0, 4))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="overline" data-testid="home-hero-overline">Arizona · Since 2006</div>
            <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[color:var(--azsam-ink)]">
              Evidence-based addiction care <em className="text-[color:var(--azsam-clay)] not-italic">for Arizona.</em>
            </h1>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-[color:var(--azsam-ink-soft)] max-w-2xl">
              The Arizona Society of Addiction Medicine (AZSAM), the Arizona Chapter of the American
              Society of Addiction Medicine, is a multidisciplinary professional organization that
              brings together addiction specialists, nurses, behavioral health specialists, researchers,
              educators, and other professionals dedicated to advancing the prevention, treatment,
              and recovery of substance use disorders. AZSAM serves as a forum for collaboration,
              professional development, and the exchange of clinical and policy expertise, while
              strengthening connections with national leaders in Addiction Medicine. Through
              education, advocacy, public awareness, and the promotion of evidence-based standards
              of care, AZSAM works to improve the quality of addiction treatment, inform public
              policy, elevate understanding of addiction as a chronic medical condition, and support
              the health and well-being of individuals, families, and communities throughout
              Arizona.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/register" className="btn-primary" data-testid="home-cta-join">
                Become a Member <ArrowRight size={18} />
              </Link>
              <Link to="/events" className="btn-outline" data-testid="home-cta-events">
                See upcoming CME
              </Link>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
              <Stat value="420+" label="Members" />
              <Stat value="7.6M" label="Arizonans" />
              <Stat value="15" label="AZ counties served" />
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-3 bg-[color:var(--azsam-clay)]/10 rounded-3xl -z-10" />
              <img
                src={heroImage}
                alt="Sedona red rocks at sunset representing Arizona's landscape"
                className="w-full h-[520px] object-cover rounded-3xl shadow-2xl"
                data-testid="home-hero-image"
              />
              <div className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur-xl rounded-xl p-5 border border-[color:var(--azsam-stone)]">
                <div className="overline">Next gathering</div>
                <div className="mt-2 font-heading text-lg" data-testid="home-next-gathering">Coming Soon</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="bg-[color:var(--azsam-cream)] border-y border-[color:var(--azsam-stone)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="max-w-2xl">
            <div className="overline">What we do</div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl leading-tight">
              A professional home built around three commitments.
            </h2>
          </div>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {pillars.map((p) => (
              <div key={p.title} className="azsam-card p-8" data-testid={`home-pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="w-11 h-11 rounded-lg bg-[color:var(--azsam-clay)]/10 text-[color:var(--azsam-clay)] flex items-center justify-center">
                  <p.icon size={22} strokeWidth={1.6} />
                </div>
                <h3 className="mt-6 font-heading text-2xl">{p.title}</h3>
                <p className="mt-3 text-sm text-[color:var(--azsam-ink-soft)] leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EVENTS PREVIEW */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <div className="overline">Coming up</div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl leading-tight">Upcoming events</h2>
          </div>
          <Link to="/events" className="text-sm font-semibold text-[color:var(--azsam-clay)] hover:underline inline-flex items-center gap-2" data-testid="home-all-events">
            All events <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {events.map((e) => (
            <Link to={`/events/${e.id}`} key={e.id} className="azsam-card overflow-hidden" data-testid={`home-event-${e.id}`}>
              <div className="h-40 overflow-hidden">
                <img src={e.image_url} alt={e.title} className="w-full h-full object-cover" />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 text-xs text-[color:var(--azsam-ink-soft)]">
                  <span className="inline-flex items-center gap-1.5"><CalendarDays size={13} />{formatDate(e.date)}</span>
                  <span className="inline-flex items-center gap-1.5"><MapPin size={13} />{e.format}</span>
                </div>
                <h3 className="mt-3 font-heading text-xl leading-snug">{e.title}</h3>
                <p className="mt-2 text-sm text-[color:var(--azsam-ink-soft)] line-clamp-3">{e.description}</p>
                {e.cme_credits > 0 && (
                  <div className="mt-4 text-xs font-semibold text-[color:var(--azsam-sage-hover)]">
                    {e.cme_credits} CME credits
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* MEET THE BOARD */}
      <section className="bg-[color:var(--azsam-cream)] border-y border-[color:var(--azsam-stone)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div className="max-w-2xl">
              <div className="overline">Leadership</div>
              <h2 className="mt-4 font-heading text-3xl sm:text-4xl leading-tight">Meet the AzSAM Board</h2>
              <p className="mt-4 text-[color:var(--azsam-ink-soft)] leading-relaxed">
                Elected annually by our members, the AzSAM Board reflects the geographic and
                disciplinary breadth of Arizona's addiction medicine community.
              </p>
            </div>
            <Link to="/about" className="text-sm font-semibold text-[color:var(--azsam-clay)] hover:underline inline-flex items-center gap-2" data-testid="home-all-board">
              Full board <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="home-board-grid">
            {board.map((m) => (
              <Link
                to="/about"
                key={m.id}
                className="azsam-card p-6 flex flex-col items-center text-center hover:-translate-y-1 transition-transform"
                data-testid={`home-board-${m.id}`}
              >
                {m.image_url ? (
                  <img
                    src={m.image_url}
                    alt={m.name}
                    className="w-24 h-24 rounded-full object-cover ring-2 ring-[color:var(--azsam-clay)]/20"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-[color:var(--azsam-sage)]/15 text-[color:var(--azsam-sage-hover)] font-heading text-xl flex items-center justify-center">
                    {m.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                  </div>
                )}
                <div className="mt-5 overline text-[color:var(--azsam-sage-hover)]">{m.title}</div>
                <h3 className="mt-2 font-heading text-lg">{m.name}</h3>
                <div className="text-xs text-[color:var(--azsam-ink-soft)] mt-1">{m.credentials}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HOW CAN WE HELP? — solid brand background */}
      <section
        className="relative overflow-hidden bg-[color:var(--azsam-ink)] text-[color:var(--azsam-sand)]"
        data-testid="home-solicit-support"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24 relative">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="overline text-[color:var(--azsam-clay)]">Partner with AzSAM</div>
              <h2 className="mt-5 font-heading text-4xl sm:text-5xl leading-tight text-[color:var(--azsam-sand)]">
                How Can We Help You?
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-[color:var(--azsam-sand)]/90 max-w-2xl">
                Meaningful change happens through collaboration. Whether you're planning an
                educational event, launching a community initiative, addressing a public policy
                issue, seeking addiction medicine expertise, or looking for ways to better support
                individuals and families affected by substance use disorders, we would love the
                opportunity to work with you.
              </p>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-black/35 backdrop-blur-xl border border-white/15 rounded-2xl p-8 shadow-2xl">
                <div className="text-xs uppercase tracking-widest text-[color:var(--azsam-clay)] font-semibold mb-4">AzSAM may be able to assist with</div>
                <ul className="space-y-3 text-sm text-[color:var(--azsam-sand)]/95">
                  <li className="flex gap-2"><span className="text-[color:var(--azsam-clay)]">→</span> Expert speakers and subject matter experts</li>
                  <li className="flex gap-2"><span className="text-[color:var(--azsam-clay)]">→</span> Educational programs, trainings, and resources</li>
                  <li className="flex gap-2"><span className="text-[color:var(--azsam-clay)]">→</span> Advocacy, policy, and public awareness initiatives</li>
                  <li className="flex gap-2"><span className="text-[color:var(--azsam-clay)]">→</span> Research, quality improvement, and community projects</li>
                  <li className="flex gap-2"><span className="text-[color:var(--azsam-clay)]">→</span> Evidence-based best practices in addiction care</li>
                </ul>
                <Link
                  to="/solicit-support"
                  className="mt-7 inline-flex items-center gap-2 w-full justify-center px-6 py-3.5 rounded-lg bg-[color:var(--azsam-clay)] hover:bg-[color:var(--azsam-clay-hover)] text-white font-semibold transition-colors"
                  data-testid="home-solicit-cta"
                >
                  Request support <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NEWS + MEMBERSHIP */}
      <section className="bg-[color:var(--azsam-cream)] border-y border-[color:var(--azsam-stone)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-3">
            <div className="flex items-end justify-between flex-wrap gap-4">
              <div>
                <div className="overline">Initiatives</div>
                <h2 className="mt-4 font-heading text-3xl sm:text-4xl">AzSAM Initiatives</h2>
              </div>
              <Link to="/news" className="text-sm font-semibold text-[color:var(--azsam-clay)] hover:underline inline-flex items-center gap-2" data-testid="home-all-news">
                All updates <ArrowRight size={16} />
              </Link>
            </div>
            <div className="mt-10 space-y-8">
              {news.map((n) => (
                <Link to={`/news/${n.id}`} key={n.id} className="grid sm:grid-cols-5 gap-6 group" data-testid={`home-news-${n.id}`}>
                  {n.image_url && (
                    <img src={n.image_url} alt="" className="sm:col-span-2 h-40 sm:h-full w-full object-cover rounded-lg" />
                  )}
                  <div className="sm:col-span-3">
                    <div className="text-xs text-[color:var(--azsam-ink-soft)]">{formatDate(n.published)} · {n.author}</div>
                    <h3 className="mt-2 font-heading text-2xl leading-snug group-hover:text-[color:var(--azsam-clay)] transition-colors">{n.title}</h3>
                    <p className="mt-2 text-sm text-[color:var(--azsam-ink-soft)] leading-relaxed">{n.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2 lg:sticky lg:top-28 self-start">
            <div className="bg-white border border-[color:var(--azsam-stone)] rounded-2xl p-8">
              <Sparkles className="text-[color:var(--azsam-clay)]" size={28} strokeWidth={1.5} />
              <h3 className="mt-4 font-heading text-2xl">Join the community</h3>
              <p className="mt-3 text-sm text-[color:var(--azsam-ink-soft)] leading-relaxed">
                Membership is open to addiction specialists, advanced practice clinicians, residents, students, and
                allied professionals committed to addiction care in Arizona.
              </p>
              <Link to="/contact" className="btn-primary mt-8 w-full justify-center" data-testid="home-join-cta">
                Contact us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <div className="font-heading text-4xl text-[color:var(--azsam-clay)]">{value}</div>
      <div className="text-xs uppercase tracking-widest text-[color:var(--azsam-ink-soft)] mt-1">{label}</div>
    </div>
  );
}

function formatDate(iso) {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
}
